import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Per-device AUDIO MIX preferences — one independent volume + mute per sound CHANNEL, so a player can
 * turn the meme soundboard down, keep voices clear, and set the radio wherever they like, each on its
 * own fader. This is the single source of truth the three audio engines read from:
 *   • `sfx`   → the soundboard MP3 reactions + the procedural entryway bell/knock (one "effects" bucket);
 *   • `voice` → the listener-side master gain over every nearby duck's voice (the flat per-peer zone gain
 *                still applies underneath — this just scales the whole channel);
 *   • `radio` → the host-controlled YouTube radio (the field the {@link RadioCard} mini-player drives).
 *
 * LOCAL & per-device only — never shared, never server-side, never a KPI (Constitution Art. 1: comfort
 * knobs, not surveillance). Persisted to localStorage like the auth + push-to-talk preferences so the
 * mix survives a refresh. Each duck owns their own ears.
 */

export type AudioChannel = 'sfx' | 'voice' | 'radio';

/** One channel's preference: a 0..100 volume and an independent mute (mute is sticky across volume edits). */
export interface ChannelPrefs {
  /** 0..100, integer. The slider value the user sees. */
  volume: number;
  /** Personal mute for this channel — kept SEPARATE from volume so unmuting restores the prior level. */
  muted: boolean;
}

interface AudioSettingsState {
  sfx: ChannelPrefs;
  voice: ChannelPrefs;
  radio: ChannelPrefs;
  /** Set a channel's volume (0..100, clamped). Leaves its mute untouched. */
  setVolume: (channel: AudioChannel, volume: number) => void;
  /** Set a channel's mute explicitly. */
  setMuted: (channel: AudioChannel, muted: boolean) => void;
  /** Flip a channel's mute. */
  toggleMuted: (channel: AudioChannel) => void;
}

const clampVolume = (v: number): number => Math.max(0, Math.min(100, Math.round(v)));

/** Effective linear gain (0..1) an engine should apply for a channel — mute wins over any volume. */
export function effectiveGain(prefs: ChannelPrefs): number {
  return prefs.muted ? 0 : clampVolume(prefs.volume) / 100;
}

// ── Legacy migration ─────────────────────────────────────────────────────────────────────────────
// The radio shipped (ADR-0021) with its OWN localStorage keys before this unified mixer existed. Seed
// the radio channel from them ONCE so an existing listener keeps their level on first load after this
// lands; thereafter the unified store is authoritative (these stale keys are simply ignored).
const LEGACY_RADIO_VOLUME_KEY = 'duck.radio.volume';
const LEGACY_RADIO_MUTED_KEY = 'duck.radio.muted';

function legacyRadioPrefs(): ChannelPrefs {
  const fallback: ChannelPrefs = { volume: 60, muted: false };
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(LEGACY_RADIO_VOLUME_KEY);
    const n = raw == null ? NaN : Number(raw);
    const volume = Number.isFinite(n) ? clampVolume(n) : fallback.volume;
    const muted = window.localStorage.getItem(LEGACY_RADIO_MUTED_KEY) === '1';
    return { volume, muted };
  } catch {
    return fallback;
  }
}

/**
 * The audio-mix store. Defaults: effects a touch below full (the recorded memes are already hot and ride
 * a limiter), voices at full (the flat per-peer zone gain sets each duck's level), radio from the legacy pref or
 * 60. Persisted under its own key; only the data channels are written (the action fns come from the
 * initializer on every load).
 */
export const useAudioSettingsStore = create<AudioSettingsState>()(
  persist(
    (set) => ({
      sfx: { volume: 80, muted: false },
      voice: { volume: 100, muted: false },
      radio: legacyRadioPrefs(),
      setVolume: (channel, volume) =>
        set(
          (s) =>
            ({ [channel]: { ...s[channel], volume: clampVolume(volume) } }) as Pick<
              AudioSettingsState,
              AudioChannel
            >,
        ),
      setMuted: (channel, muted) =>
        set(
          (s) => ({ [channel]: { ...s[channel], muted } }) as Pick<AudioSettingsState, AudioChannel>,
        ),
      toggleMuted: (channel) =>
        set(
          (s) =>
            ({ [channel]: { ...s[channel], muted: !s[channel].muted } }) as Pick<
              AudioSettingsState,
              AudioChannel
            >,
        ),
    }),
    {
      name: 'duck-duck-office-audio',
      version: 1,
      partialize: (s) => ({ sfx: s.sfx, voice: s.voice, radio: s.radio }),
    },
  ),
);
